import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chatService } from 'services/chat/chatService';
import { OfferStatus } from '../services';
import { Offer } from '../types';
import { OfferCard } from './OfferCard';

interface OffersListProps {
	offers: Offer[];
	isLoading: boolean;
	error: string | null;
	requestStatus?: OfferStatus;
	requestId: string;
	userId: string;
	onRefreshOffers?: () => void; // Add callback to refresh offers data
}

export const OffersList: React.FC<OffersListProps> = ({
	offers,
	isLoading,
	error,
	requestId,
	requestStatus = 'ongoing',
	userId,
	onRefreshOffers,
}) => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const theme = useTheme();
	const [chatLoading, setChatLoading] = useState(false);
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isCompleted = requestStatus === 'completed';
	const isArchived = requestStatus === 'archived';

	const handleChat = async (offerId: string, specialistId?: string) => {
		setChatLoading(true);
		
		// Use specialistId if provided, otherwise fall back to userId
		const userIdToUse = specialistId || userId;
		
		chatService
			.getChannelIdByRequestAndUserId(requestId, userIdToUse)
			.then((channel) => {
				navigateWithLanguage(`/farmer/chats?channelId=${channel}`);
			})
			.catch((error) => {
				console.error('[OffersList] Error fetching channel ID:', error);
			})
			.finally(() => setChatLoading(false));
	};

	// Show loading state
	if (isLoading) {
		return (
			<Box sx={{ 
				display: 'flex', 
				flexDirection: 'column', 
				alignItems: 'center', 
				justifyContent: 'center', 
				height: '300px',
				width: '100%'
			}}>
				<CircularProgress size={40} sx={{ color: '#3da08f', mb: 2 }} />
				<Typography variant="body1" sx={{ color: '#666' }}>{t('loadingOffers')}</Typography>
			</Box>
		);
	}

	// Show error state
	if (error) {
		return (
			<Box sx={{ 
				display: 'flex', 
				flexDirection: 'column', 
				alignItems: 'center', 
				justifyContent: 'center', 
				height: '300px',
				width: '100%'
			}}>
				<Typography variant="body1" sx={{ color: 'error.main', mb: 2 }}>{t('error')}</Typography>
				<Typography variant="body2" sx={{ color: '#666' }}>{error}</Typography>
			</Box>
		);
	}

	// Show empty state
	if (!offers || offers.length === 0) {
		return (
			<Box className="flex flex-col items-center justify-center h-64">
				<Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
					{t('offers')}
				</Typography>
				<Typography variant="body1" className="text-gray-600">
					{requestStatus === 'active'
						? t('noOffersToDisplay')
						: requestStatus === 'archived'
							? t('noArchivedOffersYet')
							: t('noCompletedOffersYet')}
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Typography
				variant="h6"
				sx={{
					mb: 2,
					fontWeight: 500,
					fontSize: isMobile ? '1rem' : undefined,
				}}
			>
				{t('offers')}
			</Typography>

			{/* Offers list */}
			{offers.map((offer, index) => (
				<div key={`${offer.id}_${index}`}>
					<OfferCard
						offer={offer}
						onChat={handleChat}
						isMobile={isMobile}
						isCompleted={isCompleted}
						isArchived={isArchived}
						chatLoading={chatLoading}
						onRefresh={onRefreshOffers}
					/>
				</div>
			))}
		</Box>
	);
};
